import { ref, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { getCookie } from '@/utils/cookie';
import { seomaticHead } from '@/utils/seomatic';
import { useBlackrollAuth } from './useBlackrollAuth';

/**
 * Composable for entry page functionality
 * This provides the same functionality as the entryPage mixin but for Composition API
 */
export const useEntryPage = (entry = null, seomatic = null, gtmMetaOptions) => {
  const route = useRoute();
  const { t, locale } = useI18n();
  const { $localeRootPath, $_gtm, $getCurrentLocaleConfig } = useNuxtApp();
  const config = useRuntimeConfig();
  const { loggedIn, user } = useBlackrollAuth();

  // State
  const entryData = ref(entry);
  const isRestricted = ref(false);
  const breadCrumbs = ref([]);

  /**
   * Anonymize page path for tracking
   */
  const anonymizePagePath = (route) => {
    if (!route) {
      return '';
    }

    const filterKeys = ['email', 'password', 'token', 'key'];
    const urlParams = new URLSearchParams(window.location.search);

    filterKeys.forEach((key) => {
      if (urlParams.has(key)) {
        urlParams.set(key, '***');
      }
    });

    return route.path + (urlParams.toString() ? '?' + urlParams.toString() : '');
  };

  /**
   * Get meta info title from current component
   */
  const metaInfoTitle = () => {
    return document.title;
  };

  /**
   * Push page meta data to GTM
   */
  const gtmPushPageMeta = (options = {}, preventUserUpdate = false) => {
    const defaultOptions = {
      event: 'virtPageView',
      page_type: entryData.value?.typeHandle || '--',
      page_path: anonymizePagePath(route),
      page_title: metaInfoTitle(),
      client_id: getCookie('_ga'),
      country: locale.value,
      language: locale.value,
      topic: entryData.value?.seoTopics?.join(',') || '--',
      system: 'CMS',
      environment: config.public?.gtmEnv || '--',
      content_category: entryData.value?.sectionHandle || '--',
      collection: '--',
      search_results: '--',
      product_category: '--',
      product_subcategory: '--',
      content_subcategory: '--',
    };

    if ($_gtm) {
      $_gtm.push({ ...defaultOptions, ...options });
    }

    if (!preventUserUpdate) {
      gtmPushUserMeta();
    }
  };

  /**
   * Count total orders
   */
  const countOrdersTotal = () => {
    return (
      user.value?.blackrollCustomer?.orders?.reduce((a, b) => {
        if (b.totalPrice === undefined) {
          return a;
        }

        return a + +b.totalPrice;
      }, 0) || 0
    );
  };

  /**
   * Get user country
   */
  const getUserCountry = () => {
    const defaultAddress = user.value?.blackrollCustomer?.addresses?.filter((address) => {
      return address.defaultAddress === true;
    });
    // Note: in Composition API we can't use $t directly so we use t from useI18n()
    return defaultAddress?.length ? t(`countries.${defaultAddress[0]?.country}`, 'en') : null;
  };

  /**
   * Push user meta data to GTM
   */
  const gtmPushUserMeta = () => {
    const userData = {
      event: 'User Meta',
      client_id: getCookie('_ga'),
      login_status: 'false',
    };

    if (loggedIn.value && user.value) {
      userData.user_id = user.value.uid;
      userData.user_country = getUserCountry();
      // newsletter_subscriber: '(true | false)'
      userData.user_exercises = user.value.blackrollCustomer?.exercises?.length || 0;
      userData.user_workouts = user.value.blackrollCustomer?.workouts?.length || 0;
      userData.user_registration = user.value.creationTime;
      userData.last_time_ordered = user.value.blackrollCustomer?.orders[0]?.date;
      userData.total_spent_to_date = countOrdersTotal().toFixed(2);
      userData.total_orders = user.value.blackrollCustomer?.orders?.length || 0;
      userData.login_status = 'true';
    }

    if ($_gtm) {
      $_gtm.push(userData);
    }
  };

  /**
   * Check premium content access
   */
  const checkPremiumContent = () => {
    const premiumSettings = entryData.value?.premiumContentRestrictions;

    if (premiumSettings?.length > 0) {
      premiumSettings.forEach((setting) => {
        if (setting.authenticatedOnly && !loggedIn.value) {
          window.location.replace($localeRootPath('/account/login'));
          return;
        }

        if (setting.purchasedProductOnly && loggedIn.value) {
          // check for purchased product in account data
          let hasPurchase = false;

          // if not found, redirect away
          user.value?.blackrollCustomer?.addOns?.forEach((addOn) => {
            if (addOn.handle === setting.purchasedProductOnly) {
              hasPurchase = true;
            }
          });

          if (!hasPurchase) {
            window.location.replace($localeRootPath('/account/exercises/premium'));
            return;
          } else {
            isRestricted.value = false;
          }
        }
      });
    }
  };

  /**
   * Builds breadcrumbs from parents property recursively - skips for root-entry with uri: ''
   * @returns {array} - list of objects {label: 'title' , route: {path: '/some/uri'}}
   */
  const buildBreadCrumb = () => {
    if (!entryData.value) return [];

    const getCrumbs = (entry) => {
      if (entry.uri === '') return [];
      const crumb = { label: entry.title, route: { path: $localeRootPath('/' + entry.uri) } };
      if (entry.parent !== undefined && entry.parent !== null) {
        return [...getCrumbs(entry.parent), crumb];
      } else {
        return [crumb];
      }
    };
    return getCrumbs(entryData.value);
  };

  /**
   * Set the breadcrumbs for the page
   */
  const setBreadCrumb = (customBreadcrumbs = []) => {
    // Always use the store when available to avoid hydration mismatches
    const store = useNuxtApp().$store;
    const crumbsToSet =
      customBreadcrumbs.length > 0 ? customBreadcrumbs : entryData.value?.uri === '__home__' ? [] : buildBreadCrumb();

    if (store) {
      // Always update the store (for both SSR and client)
      store.dispatch('page/setBreadCrumb', crumbsToSet);

      // For client-side reactivity only, keep the local ref in sync
      // This avoids hydration mismatches but still works for composable users
      if (import.meta.client) {
        // We don't actually need to update local state here since the BreadCrumb component
        // uses the store directly via mapState. But we'll keep it updated for composable users.
        breadCrumbs.value = crumbsToSet;
      }
    } else {
      // Fallback for when store is not available
      breadCrumbs.value = crumbsToSet;
    }
  };

  /**
   * Set child breadcrumb (extends current breadcrumbs with additional ones)
   */
  const setChildBreadCrumb = (crumbs) => {
    const fullCrumbs = [...buildBreadCrumb(), ...crumbs];
    const store = useNuxtApp().$store;

    if (store) {
      // Always update the store (for both SSR and client)
      store.dispatch('page/setBreadCrumb', fullCrumbs);

      // For client-side reactivity only, keep the local ref in sync
      if (import.meta.client) {
        breadCrumbs.value = fullCrumbs;
      }
    } else {
      breadCrumbs.value = fullCrumbs;
    }
  };

  const setAlternates = () => {
    const { $store, $localeRootPath, $route, $i18n } = useNuxtApp();
    const route = useRoute();

    if (!entryData.value) {
      $store.commit('mainNav/SET_ALTERNATES', {});
      return;
    }

    const localized =
      entryData.value?.localized?.reduce((acc, curr) => {
        if (curr.language === 'gsw-CH') {
          curr.language = 'ch-de';
        } else if (curr.language === 'gsw-FR') {
          curr.language = 'ch-fr';
        }

        acc[curr.language] = $localeRootPath(curr.uri, curr.language);
        return acc;
      }, {}) || {};

    $store.commit('mainNav/SET_ALTERNATES', {
      [$i18n.locale.value]: route.path,
      ...localized,
    });
  };

  const setupEntryMeta = () => {
    const locale = $getCurrentLocaleConfig();

    let head = {
      htmlAttrs: {
        lang: locale.language,
      },
    };

    const transformedHead = seomaticHead(seomatic, entryData.value);

    if (seomatic !== null && typeof seomatic === 'object') {
      head = { ...head, ...transformedHead };
    }

    useHead(head);
  };

  // Set up the entry
  const setEntry = (newEntry) => {
    entryData.value = newEntry;
    isRestricted.value = newEntry?.premiumContentRestrictions?.length > 0;

    // Always set breadcrumbs when entry is updated
    if (newEntry) {
      // Use the setBreadCrumb method to ensure consistency
      setBreadCrumb();
      setupEntryMeta();
      setAlternates();
    }
  };

  // Initialize with any passed entry data
  if (entry) {
    setEntry(entry);
  }

  onMounted(() => {
    if (entryData.value) {
      gtmPushPageMeta(gtmMetaOptions);
    }
  });

  return {
    // State
    seomatic,
    entry: entryData,
    isRestricted,
    breadCrumbs,

    // Methods
    setEntry,
    buildBreadCrumb,
    setBreadCrumb,
    setChildBreadCrumb,
    anonymizePagePath,
    metaInfoTitle,
    gtmPushPageMeta,
    gtmPushUserMeta,
    countOrdersTotal,
    getUserCountry,
    checkPremiumContent,
  };
};
